import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ShieldCheck, ArrowLeft } from 'lucide-react'
import DocumentUpload from '@components/web/DocumentUpload'
import Button from '@components/web/Button'
import { providerAuthService } from '@lib/supabase/providerAuth.service'

export default function ProviderIdentityVerification() {
  const navigate = useNavigate()
  const [medicalCert, setMedicalCert] = useState<File | null>(null)
  const [govId, setGovId] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ medicalCert?: string; govId?: string; general?: string }>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: typeof errors = {}
    if (!medicalCert) newErrors.medicalCert = 'Please upload your medical certificate or license'
    if (!govId) newErrors.govId = 'Please upload a government-issued ID'
    if (Object.keys(newErrors).length) { setErrors(newErrors); return }

    setErrors({})
    setIsLoading(true)
    const result = await providerAuthService.submitDocuments(medicalCert!, govId!)
    setIsLoading(false)

    if (!result.success) {
      setErrors({ general: result.error || 'Upload failed. Please try again.' })
      return
    }

    navigate('/auth/provider-verification-pending')
  }

  return (
    <div className="bg-card rounded-2xl shadow-lg p-8 w-full">
      <Link to="/auth/provider-email-verification" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="mb-6">
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <ShieldCheck size={26} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Provider Verification</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Upload your professional documents for review. This is required before you can accept appointments.
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <DocumentUpload
          label="Medical License / Certificate"
          accept="image/*,.pdf"
          value={medicalCert}
          onChange={setMedicalCert}
          error={errors.medicalCert}
          hint="Supported formats: JPG, PNG, PDF"
        />

        <DocumentUpload
          label="Government-Issued ID"
          accept="image/*,.pdf"
          value={govId}
          onChange={setGovId}
          error={errors.govId}
          hint="Supported formats: JPG, PNG, PDF"
        />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          Submit Documents
        </Button>
      </form>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Your documents are encrypted and stored securely. We do not share your data.
      </p>
    </div>
  )
}
